import React, { useEffect, useState, useMemo } from 'react';
import { MoonLoader } from 'react-spinners';
import { CheckCircle, Warning, Info } from '@phosphor-icons/react';
import { AIFeatureSettings, AIFeatureKeys } from '@Models/types';
import { DropdownOptions } from '@Models/types';
import Select from '@Profile/controls/Select';
import { useAuth } from '@Context/CognitoAuthProvider';
import { aiSettingsAPI } from '@Lib/axios/ai-usage';
import { Tooltip } from 'react-tooltip';
import colors from '@Common/assets/colors';

const AISettings: React.FC = () => {
  const [aiSettings, setAISettings] = useState<AIFeatureSettings>();
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedFeature, setSelectedFeature] =
    useState<AIFeatureKeys>('generator-testcase');

  const { logout, user } = useAuth();
  const isAdmin = user?.admin;

  const featureOptions: DropdownOptions[] = useMemo(() => {
    const baseOptions: DropdownOptions[] = [
      {
        label: 'Generator - Testcase',
        value: 'generator-testcase',
      },
      {
        label: 'Generator - Image Analyzer',
        value: 'generator-imageanalyzer',
      },
      {
        label: 'Generator - Image Generator',
        value: 'generator-image',
      },
      {
        label: 'Generator - Aha! Ticket',
        value: 'generator-ahaticket',
      },
      {
        label: 'Chatbot',
        value: 'chatbot',
      },
      {
        label: 'Analyzer',
        value: 'analyzer',
      },
    ];

    if (isAdmin) {
      baseOptions.push(
        {
          label: 'Analyzer - Weekly (Global)',
          value: 'analyzer-weekly',
        },
        {
          label: 'Artillery (Global)',
          value: 'artillery',
        }
      );
    }

    return baseOptions;
  }, [isAdmin]);

  const allGptOptions: DropdownOptions[] = [
    {
      label: 'Claude 3.5 Haiku',
      value: 'us.anthropic.claude-3-5-haiku-20241022-v1:0',
    },
    {
      label: 'Claude 3.5 Sonnet',
      value: 'us.anthropic.claude-3-5-sonnet-20240620-v1:0',
    },
    {
      label: 'Claude 3.7 Sonnet',
      value: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
    },
    {
      label: 'Amazon Nova Lite',
      value: 'us.amazon.nova-lite-v1:0',
    },
  ];

  const gptMaxTokens: Record<string, number> = {
    'us.anthropic.claude-3-7-sonnet-20250219-v1:0': 130_000,
    'us.anthropic.claude-3-5-sonnet-20240620-v1:0': 130_000,
    'us.anthropic.claude-3-5-haiku-20241022-v1:0': 130_000,
    'us.amazon.nova-lite-v1:0': 10240,
  };

  // Filter out Claude 3.5 Haiku for Image Analyzer since it doesn't support image processing
  const gptOptions =
    selectedFeature === 'generator-imageanalyzer'
      ? allGptOptions.filter(
          (option) =>
            option.value !== 'us.anthropic.claude-3-5-haiku-20241022-v1:0'
        )
      : allGptOptions;

  const regionOptions: DropdownOptions[] = [
    {
      label: 'United States (N. Virginia) us-east-1',
      value: 'us-east-1',
    },
    {
      label: 'United States (Ohio) us-east-2',
      value: 'us-east-2',
    },
  ];

  const anthropicVer: DropdownOptions[] = [
    {
      label: 'bedrock-2023-05-31',
      value: 'bedrock-2023-05-31',
    },
  ];

  const aiAPI = aiSettingsAPI(logout);

  useEffect(() => {
    fetchAISettings();
    setMessageType('');
  }, [selectedFeature]);

  // Auto-switch to compatible model if current model is incompatible for Image Analyzer
  useEffect(() => {
    if (
      selectedFeature === 'generator-imageanalyzer' &&
      aiSettings?.bedRockModel === 'us.anthropic.claude-3-5-haiku-20241022-v1:0'
    ) {
      // Switch to Claude 3.5 Sonnet as the default compatible model
      updateField(
        'bedRockModel',
        'us.anthropic.claude-3-5-sonnet-20240620-v1:0'
      );
    }
  }, [selectedFeature, aiSettings?.bedRockModel]);

  const updateField = (fieldName: keyof AIFeatureSettings, value: string) => {
    let parsedValue: string | number = value;

    if (fieldName === 'temperature') {
      parsedValue = parseFloat(value) || 0;
    } else if (fieldName === 'maxTokens') {
      parsedValue = parseInt(value) || 0;
    }

    setAISettings((prev) => ({
      ...prev,
      [fieldName]: parsedValue,
    }));
  };

  useEffect(() => {
    const model = aiSettings?.bedRockModel;
    if (!model) return;

    const maxOutputTokens = gptMaxTokens[model];
    if (!aiSettings?.maxTokens || aiSettings.maxTokens > maxOutputTokens) {
      setAISettings((prev) => ({
        ...(prev ?? {}),
        maxTokens: maxOutputTokens,
      }));
    }
  }, [aiSettings?.bedRockModel]);

  const fetchAISettings = async () => {
    setLoading(true);
    try {
      const fetchedSettings = await aiAPI.getAISettings(selectedFeature);
      setAISettings(fetchedSettings);
    } catch (error) {
      console.log('Error fetching settings!: ', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setUpdating(true);
    try {
      await aiAPI.updateAISettings(aiSettings, selectedFeature);
      setMessage('AI Settings updated');
      setMessageType('success');
    } catch (error) {
      setMessage('Error updating AI Settings');
      setMessageType('error');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="flex h-full w-full flex-col gap-3 p-2">
      <div className="flex flex-col">
        <h2 className="text-xl font-semibold text-blue-900">AI Settings</h2>
        <p className="text-sm text-blue-900">
          Here you can customize AI settings, including the model, temperature,
          and region preferences, to control response behavior and optimize
          performance for your needs.
        </p>
      </div>

      <div className="h-full">
        {loading ? (
          <span className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-5 py-20">
            <MoonLoader size={75} color="#071D49" />
            <span className="animate-pulse text-sm font-semibold tracking-wide text-gray-600">
              Loading settings...
            </span>
          </span>
        ) : (
          <>
            <div className="flex w-full flex-col rounded-lg border border-gray-400 bg-[#fbfcff] p-4">
              <div className="flex flex-col text-sm font-medium text-gray-700">
                {/* Model Field */}
                <Select
                  updateField={(val) =>
                    setSelectedFeature(val as AIFeatureKeys)
                  }
                  label="Feature"
                  options={featureOptions}
                  value={selectedFeature}
                />

                {selectedFeature === 'generator-image' ? (
                  <>
                    <Select
                      updateField={(val) => updateField('bedRockModel', val)}
                      label={
                        <div className="flex items-center gap-2">
                          Classification Model
                          <button
                            data-tooltip-id="classification-model-info"
                            data-tooltip-content="Analyzes images and classifies operations (e.g., inpainting, outpainting, background removal)."
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Info size={16} weight="bold" />
                          </button>
                        </div>
                      }
                      options={gptOptions}
                      value={aiSettings?.bedRockModel}
                    />
                    <Select
                      updateField={() => null}
                      label={
                        <div className="flex items-center gap-2">
                          Image Model
                          <button
                            data-tooltip-id="generation-model-info"
                            data-tooltip-content="Generates the actual images."
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Info size={16} weight="bold" />
                          </button>
                        </div>
                      }
                      options={[
                        {
                          label: 'Amazon Titan Image Generator V2',
                          value: 'amazon.titan-image-generator-v2:0',
                        },
                      ]}
                      value="amazon.titan-image-generator-v2:0"
                    />
                    <Tooltip
                      id="classification-model-info"
                      style={{
                        color: 'white',
                        background: colors.primary[700],
                        borderRadius: '0.5rem',
                        maxWidth: '300px',
                      }}
                      className="font-medium"
                    />
                    <Tooltip
                      id="generation-model-info"
                      style={{
                        color: 'white',
                        background: colors.primary[700],
                        borderRadius: '0.5rem',
                        maxWidth: '300px',
                      }}
                      className="font-medium"
                    />
                  </>
                ) : (
                  <Select
                    updateField={(val) => updateField('bedRockModel', val)}
                    label="Model"
                    options={gptOptions}
                    value={aiSettings?.bedRockModel}
                  />
                )}

                <Select
                  updateField={(val) => updateField('region', val)}
                  label="Region"
                  options={regionOptions}
                  value={aiSettings?.region}
                />

                {/* Anthropic Version - Only show for Anthropic models */}
                {aiSettings?.bedRockModel &&
                  aiSettings.bedRockModel !== 'us.amazon.nova-lite-v1:0' && (
                    <Select
                      updateField={() => null}
                      label="Anthropic Version"
                      options={anthropicVer}
                      value={aiSettings?.anthropicVersion}
                    />
                  )}

                {/* Temperature Field */}
                <div className="flex flex-col gap-4 rounded p-2 hover:bg-[#edf7ff] sm:flex-row">
                  <label className="flex py-1">Temperature</label>
                  <div className="flex w-full items-center gap-2">
                    <input
                      type="number"
                      onChange={(e) =>
                        updateField('temperature', e.target.value)
                      }
                      value={aiSettings?.temperature}
                      step={0.1}
                      min={0}
                      max={1}
                      className="w-full rounded border border-gray-300 p-2 py-1 font-normal text-gray-600 outline-none"
                    />
                    <input
                      type="range"
                      onChange={(e) =>
                        updateField('temperature', e.target.value)
                      }
                      max={1}
                      min={0}
                      step={0.1}
                      value={aiSettings?.temperature}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Max Tokens Field - Show for all models with different limits */}
                {aiSettings?.bedRockModel && (
                  <div className="flex flex-col gap-4 rounded p-2 hover:bg-[#edf7ff] sm:flex-row">
                    <label className="flex py-1">Max Tokens</label>
                    <div className="flex flex-grow items-center gap-2">
                      <input
                        type="number"
                        onChange={(e) =>
                          updateField('maxTokens', e.target.value)
                        }
                        value={aiSettings?.maxTokens}
                        step={1}
                        min={2000}
                        max={gptMaxTokens[aiSettings.bedRockModel]}
                        className="w-full rounded border border-gray-300 p-2 py-1 font-normal text-gray-600 outline-none"
                      />
                      <input
                        type="range"
                        onChange={(e) =>
                          updateField('maxTokens', e.target.value)
                        }
                        max={gptMaxTokens[aiSettings.bedRockModel]}
                        min={2000}
                        step={1}
                        value={aiSettings?.maxTokens}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}

                {/* Error/Success Messages */}
                {messageType === 'error' && (
                  <div className="mb-3 flex items-center justify-center font-bold text-red-500">
                    <Warning size={20} weight="bold" className="mr-2" />
                    <span>{message}</span>
                  </div>
                )}
                {messageType === 'success' && (
                  <div className="mb-3 flex items-center justify-center font-bold text-green-500">
                    <CheckCircle size={20} weight="bold" className="mr-2" />
                    <span>{message}</span>
                  </div>
                )}

                {/* Submit Button */}
                <div className="mx-2 mt-4 flex justify-end gap-2">
                  <button
                    onClick={handleSubmit}
                    className={`rounded px-4 py-2 text-white ${
                      updating
                        ? `cursor-not-allowed bg-blue-300`
                        : `bg-primary-700 hover:bg-blue-500`
                    }`}
                    disabled={loading || updating}
                    data-testid="handle-submit-btn"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AISettings;
